import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, BadgeCheck, ArrowRight } from "lucide-react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Container, Button, Reveal } from "../Components/UI";
import { getPost } from "./posts";

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPost(slug) : undefined;

  // Start each article at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <main className="bg-ink-950 pt-16 md:pt-20">
      <Navbar />

      <article className="pb-20">
        {/* Header */}
        <Container className="!max-w-3xl pt-12">
          <Reveal>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-brand-400"
            >
              <ArrowLeft className="h-4 w-4" /> All articles
            </Link>

            <div className="mt-6">
              <span className="rounded-full bg-brand-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-400">
                {post.category}
              </span>
            </div>

            <h1 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
              {post.title}
            </h1>
            <p className="mt-4 text-lg text-zinc-400">{post.subtitle}</p>

            <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-3 border-y border-white/10 py-4 text-sm text-zinc-400">
              <span className="flex items-center gap-2.5">
                <img
                  src="/myPic.jpg"
                  alt="Devyansh Agrawal"
                  className="h-9 w-9 rounded-full object-cover"
                />
                <span className="font-medium text-zinc-200">Devyansh Agrawal</span>
              </span>
              <span>{post.date}</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-4 w-4" /> {post.readMins} min read
              </span>
            </div>
          </Reveal>
        </Container>

        {/* Cover */}
        <Container className="!max-w-4xl">
          <Reveal>
            <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
              <img
                src={post.cover}
                alt={post.title}
                loading="eager"
                decoding="async"
                className="aspect-[16/9] w-full object-cover object-[50%_42%]"
              />
            </div>
          </Reveal>
        </Container>

        {/* Publisher note */}
        <Container className="!max-w-3xl">
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-brand-500/20 bg-brand-500/5 px-4 py-3 text-sm text-zinc-300">
            <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-400" />
            <span>{post.publisherNote} — {post.publisher}.</span>
          </div>
        </Container>

        {/* Body */}
        <Container className="!max-w-3xl">
          <div className="mt-8">
            {post.body.map((block, i) =>
              block.type === "h" ? (
                <h2
                  key={i}
                  className="mt-12 font-display text-2xl font-bold tracking-tight text-white"
                >
                  {block.text}
                </h2>
              ) : (
                <p
                  key={i}
                  className="mt-5 text-[1.05rem] leading-8 text-zinc-300"
                >
                  {block.text}
                </p>
              )
            )}

            <p className="mt-10 border-l-2 border-brand-500 pl-4 font-display text-lg font-semibold text-white">
              — Devyansh Agrawal
            </p>
          </div>
        </Container>

        {/* CTA */}
        <Container className="!max-w-3xl">
          <div className="mt-14 flex flex-col items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-10 text-center">
            <h3 className="font-display text-xl font-semibold text-white">
              Want to build the future of clean energy?
            </h3>
            <p className="max-w-md text-sm text-zinc-400">
              Explore DevAnanta R&D, or reach out for partnerships and collaborations.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button href="https://devananta.com/" external>
                Explore DevAnanta
              </Button>
              <Button href="/connect_me" variant="ghost">
                Get in touch <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Container>
      </article>

      <Footer />
    </main>
  );
};

export default BlogPost;
